import React, { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { ConditionDetailDrawer } from './components/ConditionDetailDrawer';
import { ConditionsCard } from './components/ConditionsCard';
import { useFhirPatientId, usePatientConditions } from './data/conditions';
import { buildProblemList, findConditionByToken } from './problem-list';

/**
 * The Conditions vertical slice for the new patient profile Overview tab:
 * resolves the FHIR Patient from the route's (Hasura) patient id, loads the
 * condition connection, and owns the card + detail drawer. The open drawer
 * lives in the URL (`?condition=<id>`) so detail views stay deep-linkable —
 * routing, not component state, is the source of truth.
 */
const DRAWER_PARAM = 'condition';

export const PatientConditionsSection: React.FC = () => {
  const { patientId } = useParams() as { patientId: string };
  const [searchParams, setSearchParams] = useSearchParams();

  const fhirPatient = useFhirPatientId(patientId);
  const conditions = usePatientConditions(fhirPatient.data ?? null);

  const list = useMemo(
    () => buildProblemList(conditions.data ?? []),
    [conditions.data],
  );

  const openToken = searchParams.get(DRAWER_PARAM);
  const openCondition = findConditionByToken(list, openToken);

  const openDrawer = useCallback(
    (token: string) => {
      setSearchParams(
        (params) => {
          params.set(DRAWER_PARAM, token);
          return params;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const closeDrawer = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete(DRAWER_PARAM);
        return params;
      },
      { replace: false },
    );
  }, [setSearchParams]);

  return (
    <>
      <ConditionsCard
        list={list}
        loading={fhirPatient.loading || conditions.loading}
        failed={fhirPatient.failed || conditions.failed}
        paging={conditions}
        onOpenCondition={openDrawer}
      />
      <ConditionDetailDrawer
        condition={openCondition}
        open={Boolean(openToken && openCondition)}
        onClose={closeDrawer}
        onReviewed={conditions.refetch}
      />
    </>
  );
};
